import type { LatLng } from './crane';
import { haversineMeters } from './driver-attendance';

/**
 * Bản đồ xe cho Điều phối (thiết kế "Bản đồ xe cho Điều phối" 14/10/2026, anh Tiến duyệt; docs/phases/P11.md §12).
 * Hàm thuần: phân loại trạng thái xe, khoảng cách ước tính, xếp xe gần nhất cho 1 phiếu chờ.
 */
export const VEHICLE_MAP_STATUSES = ['off', 'at_customer', 'moving', 'idle'] as const;
export type VehicleMapStatus = (typeof VEHICLE_MAP_STATUSES)[number];
export const VEHICLE_MAP_STATUS_LABELS: Record<VehicleMapStatus, string> = {
  off: 'Nghỉ / bảo dưỡng',
  at_customer: 'Đang giao tại khách',
  moving: 'Đang chạy',
  idle: 'Rảnh',
};

/** Xe đứng yên trong bán kính này quanh 1 điểm giao của chuyến → Đang giao tại khách */
export const AT_CUSTOMER_RADIUS_M = 300;
/** Không lấy được tuyến đường: km đường thẳng × hệ số này, gắn chữ "ước tính" */
export const MAP_ROAD_FACTOR = 1.3;
/** Tốc độ bình quân dùng ước tính phút khi không có Google (km/h) */
export const MAP_FALLBACK_SPEED_KMH = 30;
/** Chuyến đang chạy còn tối đa bấy nhiêu điểm giao chưa xong → "sắp xong" (được đề xuất nhận thêm phiếu) */
export const NEARLY_DONE_MAX_DROPS = 1;

export interface VehicleStatusInput {
  /** Xe bị khóa (checklist nhận xe không đạt…) – không được chạy */
  locked: boolean;
  /** Có phiếu chi phí Bảo dưỡng / Sửa chữa ghi ngày hôm nay đã duyệt */
  maintenanceToday: boolean;
  /** Tài xế yêu cầu bảo dưỡng và quản lý đã duyệt, chưa bấm Bảo dưỡng xong (DEC-740) */
  serviceApproved?: boolean;
  /** Có tài xế đang nhận xe (lần bàn giao cuối trong ngày là Nhận xe) */
  hasDriverToday: boolean;
  position: (LatLng & { speedKmh: number | null }) | null;
  /** Có chuyến Đang chạy */
  tripInProgress: boolean;
  /** Số phiếu Đang giao gán thẳng cho xe (không qua chuyến) */
  deliveringNotes: number;
  /** Điểm giao (có tọa độ) của các chuyến chưa đóng và phiếu đang giao trên xe */
  dropPoints: LatLng[];
}

export interface VehicleStatusResult {
  status: VehicleMapStatus;
  /** Lý do ngắn hiện trong popup */
  reason: string;
  /** Vị trí trong dropPoints đang đứng gần (Đang giao tại khách) */
  atDropIndex: number | null;
}

/**
 * Trạng thái xe theo thứ tự ưu tiên (anh Tiến 02/11/2026: xe đã nhận, đang giao mà hiện Rảnh / Nghỉ – bảo dưỡng là sai):
 * 1. Xe bị khóa → Nghỉ / bảo dưỡng.
 * 2. Đang giao / đang lấy hàng (chuyến Đang chạy hoặc phiếu Đang giao trên xe): đứng yên gần điểm giao → Đang giao tại khách,
 *    còn lại → Đang chạy.
 * 3. GPS báo đang chạy → Đang chạy (kể cả khi tài xế chưa bấm Nhận xe, hoặc có phiếu bảo dưỡng trong ngày).
 * 3b. Tài xế yêu cầu bảo dưỡng đã được duyệt (DEC-740) → Nghỉ / bảo dưỡng.
 * 4. Đứng yên gần điểm giao của chuyến chưa chạy (đã có tài) → Đang giao tại khách.
 * 5. Tài xế đã nhận xe hôm nay → Rảnh (phiếu bảo dưỡng trong ngày không làm xe thành Nghỉ, vì xe đã ra khỏi xưởng).
 * 6. Có phiếu bảo dưỡng / sửa chữa hôm nay, chưa ai nhận xe → Nghỉ / bảo dưỡng.
 * 7. Chưa có tài xế nhận xe → Nghỉ / bảo dưỡng.
 */
export function classifyVehicleMapStatus(i: VehicleStatusInput): VehicleStatusResult {
  if (i.locked) return { status: 'off', reason: 'Xe đang khóa', atDropIndex: null };
  const p = i.position;
  const moving = !!p && p.speedKmh !== null && p.speedKmh > 0;
  const near = () => {
    if (!p || moving) return null;
    let bestIdx = -1;
    let bestM = AT_CUSTOMER_RADIUS_M;
    for (let idx = 0; idx < i.dropPoints.length; idx++) {
      const d = i.dropPoints[idx]!;
      const m = haversineMeters(p.lat, p.lng, d.lat, d.lng);
      if (m < bestM) {
        bestM = m;
        bestIdx = idx;
      }
    }
    return bestIdx >= 0 ? ({ status: 'at_customer', reason: `Đứng yên cách điểm giao ${Math.round(bestM)} m`, atDropIndex: bestIdx } as const) : null;
  };
  if (i.tripInProgress || i.deliveringNotes > 0) {
    const at = near();
    if (at) return at;
    return {
      status: 'moving',
      reason: i.tripInProgress ? 'Chuyến đang chạy' : `Đang giao ${i.deliveringNotes} phiếu`,
      atDropIndex: null,
    };
  }
  if (moving) return { status: 'moving', reason: `GPS báo ${p!.speedKmh} km/h`, atDropIndex: null };
  if (i.serviceApproved) return { status: 'off', reason: 'Tài yêu cầu bảo dưỡng (đã duyệt)', atDropIndex: null };
  if (i.hasDriverToday) {
    const at = near();
    if (at) return at;
    return {
      status: 'idle',
      reason: i.maintenanceToday ? 'Đã nhận xe, không có chuyến đang chạy (có phiếu bảo dưỡng hôm nay)' : 'Không có chuyến đang chạy',
      atDropIndex: null,
    };
  }
  if (i.maintenanceToday) return { status: 'off', reason: 'Đang bảo dưỡng / sửa chữa', atDropIndex: null };
  return { status: 'off', reason: 'Chưa có tài xế nhận xe hôm nay', atDropIndex: null };
}

/** Km đường thẳng × 1,3 (1 số lẻ) và phút theo tốc độ bình quân – dùng khi không có Google. */
export function estimateRoad(a: LatLng, b: LatLng): { km: number; minutes: number } {
  const km = Math.round((haversineMeters(a.lat, a.lng, b.lat, b.lng) / 1000) * MAP_ROAD_FACTOR * 10) / 10;
  return { km, minutes: Math.round((km / MAP_FALLBACK_SPEED_KMH) * 60) };
}

export interface NearestCandidate {
  vehicleId: number;
  status: VehicleMapStatus;
  /** Có chuyến Đang chạy */
  hasRunningTrip: boolean;
  /** Chuyến đang chạy còn bao nhiêu điểm giao chưa xong (0 khi không chạy) */
  remainingDrops: number;
  /** Tải còn trống (kg); null = xe chưa khai tải */
  freeKg: number | null;
  /** Km tới phiếu (đường bộ hoặc ước tính); null = xe chưa có GPS */
  km: number | null;
}

/** Xe được đề xuất cho phiếu chờ: Rảnh, hoặc chuyến đang chạy sắp xong; còn tải ≥ kg phiếu (xe chưa khai tải: không chặn). */
export function nearestEligible(c: NearestCandidate, noteKg: number): { ok: boolean; reason: string | null } {
  if (c.status === 'off') return { ok: false, reason: 'Xe nghỉ / bảo dưỡng' };
  if (c.status !== 'idle') {
    if (!c.hasRunningTrip) return { ok: false, reason: 'Xe đang chạy, không có chuyến trên ERP' };
    if (c.remainingDrops > NEARLY_DONE_MAX_DROPS) return { ok: false, reason: `Còn ${c.remainingDrops} điểm giao` };
  }
  if (c.freeKg !== null && c.freeKg < noteKg) return { ok: false, reason: `Tải trống ${Math.round(c.freeKg)} kg < phiếu ${Math.round(noteKg)} kg` };
  return { ok: true, reason: null };
}

/** Sắp theo km tăng dần, xe chưa có GPS xuống cuối; bằng km thì xe Rảnh trước. */
export function sortNearest<T extends Pick<NearestCandidate, 'km' | 'status' | 'vehicleId'>>(list: T[]): T[] {
  return [...list].sort((a, b) => {
    if (a.km === null && b.km !== null) return 1;
    if (b.km === null && a.km !== null) return -1;
    if (a.km !== null && b.km !== null && a.km !== b.km) return a.km - b.km;
    const ia = a.status === 'idle' ? 0 : 1;
    const ib = b.status === 'idle' ? 0 : 1;
    return ia - ib || a.vehicleId - b.vehicleId;
  });
}

/** Màu kho trên Bản đồ xe (anh Tiến 15/10/2026: mỗi kho một màu). Khác bảng màu đường chuyến để không lẫn. */
export const WAREHOUSE_MAP_COLORS = ['#b91c1c', '#15803d', '#a16207', '#6d28d9', '#c2410c', '#0369a1', '#be185d', '#4d7c0f', '#334155', '#0f766e'] as const;

/** Gán màu kho theo thứ tự id tăng dần: cùng danh mục kho thì màu không đổi giữa các lần mở. Quá 10 kho thì quay vòng. */
export function warehouseMapColors(ids: number[]): Map<number, string> {
  const sorted = [...new Set(ids)].sort((a, b) => a - b);
  return new Map(sorted.map((id, i) => [id, WAREHOUSE_MAP_COLORS[i % WAREHOUSE_MAP_COLORS.length]!]));
}

// ---------------------------------------------------------------------------
// Bản đồ điều phối – kế hoạch xuất phát từ bãi (anh Tiến 04/11/2026: "bản đồ xếp xe ngày hôm sau, tính từ bãi đậu xe"; DEC-890)
// ---------------------------------------------------------------------------

/**
 * Điểm giao cách bãi / kho xa hơn ngưỡng này: không đưa vào khung bản đồ và không tính km lộ trình (thường là định vị sai,
 * ví dụ phiếu 016-GH-041126 định vị ở Hải Phòng làm bản đồ thu ra cả châu Á). Vẫn liệt kê để sửa định vị.
 */
export const MAP_PLAN_FAR_KM = 150;

/** Km đường thẳng giữa 2 điểm (1 số lẻ) */
export const straightKm = (a: LatLng, b: LatLng) => Math.round(haversineMeters(a.lat, a.lng, b.lat, b.lng) / 100) / 10;

/**
 * Điểm có xa mọi mốc (bãi xe, kho) hơn `maxKm` không. Không có mốc nào → không coi là xa.
 */
export function isFarFromAnchors(p: LatLng, anchors: LatLng[], maxKm = MAP_PLAN_FAR_KM): boolean {
  if (!anchors.length) return false;
  return anchors.every((a) => straightKm(a, p) > maxKm);
}

/** Lọc các điểm dùng để thu khung bản đồ: bỏ điểm xa mọi mốc (định vị lệch). Mọi điểm đều xa → giữ nguyên để vẫn thấy. */
export function pointsForFit<T extends LatLng>(points: T[], anchors: LatLng[], maxKm = MAP_PLAN_FAR_KM): T[] {
  const near = points.filter((p) => !isFarFromAnchors(p, anchors, maxKm));
  return near.length ? near : points;
}

export interface PlanLegInput {
  /** Km + phút chặng tới điểm này (null = điểm không có tọa độ / bị loại vì xa) */
  leg: { km: number; minutes: number | null } | null;
}

/**
 * Giờ tới từng điểm (phút trong ngày) theo thứ tự giao: xuất bãi lúc `departMinute`, mỗi chặng cộng phút chạy,
 * mỗi điểm đã giao cộng `unloadMinutes` phút dỡ hàng trước khi đi tiếp. Điểm không có chặng → null, không cộng giờ.
 * Chặng thiếu phút (Google không trả) ước tính theo MAP_FALLBACK_SPEED_KMH.
 */
export function planArrivals(stops: PlanLegInput[], departMinute: number, unloadMinutes: number): (number | null)[] {
  let t = departMinute;
  return stops.map((s) => {
    if (!s.leg) return null;
    t += s.leg.minutes ?? Math.round((s.leg.km / MAP_FALLBACK_SPEED_KMH) * 60);
    const at = t;
    t += unloadMinutes;
    return at;
  });
}

/** Phút trong ngày → "HH:mm" (quá 24h vẫn cộng dồn, ví dụ 25:10) */
export const minuteToHHmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(Math.round(m) % 60).padStart(2, '0')}`;
